import { Button, Input } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useCommunicationMeetingsQuery } from "../api/queries";
import { CommunicationTour } from "../tour/CommunicationTour";
import { meetingsTourSteps } from "../tour/communicationTourSteps";
import { useCommunicationTour } from "../tour/useCommunicationTour";
import { MeetingRowItem } from "../ui/MeetingRowItem";

export function CommunicationMeetingsPage() {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const tour = useCommunicationTour("communication-meetings-tour-completed");
  const { data, isLoading } = useCommunicationMeetingsQuery({
    limit: 50,
    offset: 0,
  });
  const items = data?.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;
    return items.filter((meeting) =>
      [meeting.title, meeting.summary ?? "", meeting.organizerName ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [items, normalizedSearch]);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div data-tourid="communication-meetings-title">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                ກອງປະຊຸມຂອງທ່ານ
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດລອງການນຳທາງ"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ກອງປະຊຸມທີ່ທ່ານເຂົ້າຮ່ວມ ຫຼື ເປັນຜູ້ຈັດ — ເບິ່ງສະຫຼຸບແລະເປີດບັນທຶກການປະຊຸມໄດ້ຈາກບ່ອນນີ້.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: "/app/dashboard" })}
          >
            <ArrowLeft className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div>
          <div className="mb-4">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                placeholder="ຄົ້ນຫາກອງປະຊຸມ..."
              />
            </div>
          </div>
          <div
            data-tourid="communication-meetings-list"
            className="divide-y divide-border"
          >
            {isLoading ? (
              Array.from({ length: 5 }, (_, i) => `meeting-skeleton-${i}`).map(
                (key) => (
                  <div
                    key={key}
                    className="h-24 animate-pulse rounded-md bg-muted/60 py-4"
                  />
                ),
              )
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                ບໍ່ມີຂໍ້ມູນກອງປະຊຸມ.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                ບໍ່ພົບຂໍ້ມູນຕາມຄຳຄົ້ນຫາ.
              </p>
            ) : (
              filteredItems.map((meeting) => (
                <MeetingRowItem
                  key={meeting.id}
                  title={meeting.title}
                  meetingDate={meeting.meetingDate}
                  status={meeting.status}
                  summary={meeting.summary}
                  organizerName={meeting.organizerName}
                  onOpen={() =>
                    navigate({
                      to: "/app/communication/minutes/$id",
                      params: { id: meeting.id },
                      search: { from: "meetings" },
                    })
                  }
                />
              ))
            )}
          </div>
        </div>

        <CommunicationTour
          steps={meetingsTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
